import {
  CLEAR_FILTER_TOKEN,
  FILTER_CONCATENATE_TOKEN,
  PromptCard,
  PromptTabs,
  PromptTeamNotice,
  RemoveFromWorkgroupDialog,
  type Filter,
  type Prompt,
} from "@1js/pl-card-components";
import * as React from "react";

import { useStrings } from "@1js/localization-components";
import { Column, PromptCardSkeleton, Row } from "@1js/pl-card-components";
import type { Group } from "@1js/pl-types";
import { Filter24Regular } from "@fluentui/react-icons";
import {
  PromptRemovedFromTeamToastErrorTitle,
  PromptRemovedFromTeamToastTitle,
} from "../../pages/prompts/allPromptsLibrary/AllPromptsLibrary.web.string";
import { IsTeamsTab, type SearchResultsData } from "../../types";
import { MosSubHeading } from "../MosSubheading/MosSubheading.web";
import { PromptDetailModal } from "../PromptDetail/PromptDetailModal.web";
import type { WebPrompt } from "../data";
import type { HandlePromptsTabSelected, Notify } from "./AllPrompts.web.data";
import { useStyles } from "./AllPrompts.web.styles";
import { EmptyStatePromptResults } from "./EmptyStatePromptResults";
import { Filters } from "./Filters.web";
import { PromptHeadingV2 } from "./PromptHeadingV2";
import { ShowMore } from "./ShowMore.web";
import { useWorkgroupPrompts } from "./WorkgroupPrompts.hook.web";
import { useColumnStyle } from "./useColumnStyle.hook";

export type WorkgroupPromptsProps = {
  currentFocusedPromptId: string | null;
  setCurrentFocusedPromptId: (promptId: string) => void;
  isLoading: boolean;
  skeletonCount: number;
  promptsToDisplay: Prompt[];
  promptRefs: React.MutableRefObject<
    Map<string, React.RefObject<HTMLDivElement>>
  >;
  handleCardClick: (prompt: Prompt) => void;
  setLastFocusedPromptId: (promptId: string) => void;
  groups?: Group[];
  onMoreClicked: () => void;
  isShowHasMore: boolean;
  hasMore?: boolean;
  handleCardFocus: () => void;
  resultsAnnouncementId: string;
  swipe: {
    setSwipedState: (swiped: boolean) => void;
  };
  dialogOpen: boolean;
  handleDialogClose: () => void;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  activePrompt: WebPrompt | undefined;
  morePrompts: WebPrompt[];
  onCopyPromptClicked: ((prompt: Prompt) => void) | undefined;
  onTryInAppBadgeAction?: (
    prompt: Prompt,
    product: string,
    tagRef: React.MutableRefObject<null>
  ) => void;
  isShowFilters: boolean;
  onGroupPromptsFilterChanged: (
    filter: Record<string, string[]>,
    groups?: Group[]
  ) => void;
  handleGroupPromptsFilterOnOpenChange: (isOpen: boolean) => void;
  filters?: Filter[] | undefined;
  selectedFacets: Record<string, string[]>;
  handleFilterChange: (
    selectedValues: Record<string, string[]>,
    filters: Filter[]
  ) => void;
  children: React.ReactNode[] | React.ReactNode;
  notify: Notify;
  handlePromptsTabSelected: HandlePromptsTabSelected;
  onFiltersCleared: () => void;
  onFiltersOpenChanged: (isOpen: boolean, filters: Filter[]) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  onPromptBookmarkToggled: (prompt: Prompt) => Promise<void>;
  isMetaOsV2?: boolean;
  searchResultsData?: SearchResultsData;
  isMetaOsV2HomePage?: boolean;
};

export const WorkgroupPrompts: React.FC<WorkgroupPromptsProps> = ({
  currentFocusedPromptId,
  setCurrentFocusedPromptId,
  isLoading,
  skeletonCount,
  promptsToDisplay,
  promptRefs,
  handleCardClick,
  setLastFocusedPromptId,
  groups,
  onMoreClicked,
  isShowHasMore,
  hasMore,
  handleCardFocus,
  resultsAnnouncementId,
  dialogOpen,
  handleDialogClose,
  setDialogOpen,
  activePrompt,
  morePrompts,
  onCopyPromptClicked,
  onTryInAppBadgeAction,
  isShowFilters,
  filters,
  onGroupPromptsFilterChanged,
  handleFilterChange,
  children,
  notify,
  handlePromptsTabSelected,
  selectedFacets,
  onFiltersOpenChanged,
  onFiltersCleared,
  handleKeyDown,
  onPromptBookmarkToggled,
  isMetaOsV2,
  searchResultsData,
  isMetaOsV2HomePage,
}: WorkgroupPromptsProps) => {
  const styles = useStyles();
  const localization = useStrings({
    PromptRemovedFromTeamToastErrorTitle,
    PromptRemovedFromTeamToastTitle,
  });
  const {
    handleOnRemoveClose,
    handleOnRemoveDialogClicked,
    handleOnRemoveFromWorkgroupClicked,
    removeDialogConfig,
    isFlightActive,
    handleFilterChanged: legacyHandleFilterChanged,
    chooseBestProductForHandoff,
  } = useWorkgroupPrompts({
    notify,
    promptRemovedFromTeamToastErrorTitle:
      localization.PromptRemovedFromTeamToastErrorTitle,
    promptRemovedFromTeamToastTitle:
      localization.PromptRemovedFromTeamToastTitle,
    handlePromptsTabSelected,
    setDialogOpen,
    promptRefs,
    setCurrentFocusedPromptId,
    onGroupPromptsFilterChanged,
  });

  const appearance = isFlightActive("isMosAppPromptCardV2Enabled")
    ? "web-v3"
    : "web";
  const cardColStyles = useColumnStyle();
  const isTeamsTab = localStorage.getItem(IsTeamsTab) === "true" ? true : false;
  const selectedDialogPromptWorkgroup =
    selectedFacets.workgroup.filter((selectedGroup) =>
      activePrompt?.Groups?.some((group) => group.Id === selectedGroup)
    )[0] ?? activePrompt?.Groups?.[0]?.Id;

  // Check for inactive filters(only default select all is applied)
  const inActiveFilter = Object.values(selectedFacets).every(
    (values) =>
      Array.isArray(values) &&
      values.every((value) => {
        const [firstToken] = value.split(FILTER_CONCATENATE_TOKEN);
        return firstToken === CLEAR_FILTER_TOKEN;
      })
  );

  // Showing an empty state when no prompts are available without any filters on load
  if (isMetaOsV2 && inActiveFilter && promptsToDisplay.length === 0) {
    return (
      <>
        {!isTeamsTab ? (
          <PromptHeadingV2
            navItem={PromptTabs.GroupPrompts}
            searchResultsData={searchResultsData}
            isMetaOsV2HomePage={isMetaOsV2HomePage}
          />
        ) : (
          <MosSubHeading />
        )}
        ;
        <EmptyStatePromptResults navItem={PromptTabs.GroupPrompts} />
      </>
    );
  }

  return (
    <>
      {isMetaOsV2 && !isTeamsTab && (
        <PromptHeadingV2
          navItem={PromptTabs.GroupPrompts}
          searchResultsData={searchResultsData}
          isMetaOsV2HomePage={isMetaOsV2HomePage}
        />
      )}
      {isTeamsTab && <MosSubHeading />}
      <RemoveFromWorkgroupDialog
        isOpen={removeDialogConfig.isOpen}
        onClose={handleOnRemoveClose}
        onRemove={handleOnRemoveDialogClicked}
        group={
          isFlightActive("isUnifiedWorkgroupPromptsViewEnabled")
            ? groups?.find(
                (group) => group.Id === removeDialogConfig.groupIds?.[0]
              )
            : undefined
        }
      />
      {isShowFilters && (
        <div className={styles.filtersRow}>
          {groups && filters && !isLoading && (
            <>
              {isMetaOsV2 && <Filter24Regular className={styles.filterIcon} />}
              <Filters
                filters={filters}
                selectedFilters={selectedFacets}
                onFilterChanged={
                  isFlightActive("isUnifiedWorkgroupPromptsViewEnabled")
                    ? handleFilterChange
                    : legacyHandleFilterChanged
                }
                onCleared={onFiltersCleared}
                onOpenChanged={onFiltersOpenChanged}
                isMetaOsV2={isMetaOsV2}
              />
            </>
          )}
          <div className={styles.filtersContainer}></div>
        </div>
      )}
      {isFlightActive("isTeamAnnouncementEnabled") && <PromptTeamNotice />}
      {children}
      {isLoading ? (
        <Row className={styles.cards} isMetaOsV2={isMetaOsV2}>
          {/* create skeletonCount number of PromptCardSkelton component */}
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <Column key={index} size={3} className={cardColStyles}>
              <PromptCardSkeleton appearance={appearance} />
            </Column>
          ))}
        </Row>
      ) : (
        <>
          <Row
            role="list"
            aria-label="Prompt"
            aria-describedby={resultsAnnouncementId}
            className={styles.cards}
            data-testid="prompt-cards"
            onKeyDown={handleKeyDown}
          >
            {promptsToDisplay.map((prompt: Prompt, idx: number) => {
              if (!promptRefs.current.has(prompt.Id)) {
                promptRefs.current.set(prompt.Id, React.createRef());
              }
              const handleFocus = () => {
                setLastFocusedPromptId(prompt.Id);
                setCurrentFocusedPromptId(prompt.Id);
              };
              const selectedPromptWorkgroup =
                selectedFacets.workgroup.filter((selectedGroup) =>
                  prompt.Groups?.some((group) => group.Id === selectedGroup)
                )[0] ?? prompt.Groups?.[0]?.Id;
              return (
                <Column key={prompt.Id} size={3} className={cardColStyles}>
                  <PromptCard
                    appearance={appearance}
                    key={prompt.Id}
                    displayPosition={idx}
                    isPromptFocused={currentFocusedPromptId === prompt.Id}
                    onFocus={handleFocus}
                    prompt={prompt}
                    isPromptRemovingFromWorkgroupEnabled={true}
                    isFavoriteEnabled={isFlightActive("isPromptLikingEnabled")}
                    isPromptLikingEnabled={isFlightActive(
                      "isPromptLikingEnabled"
                    )}
                    onFavoriteToggled={onPromptBookmarkToggled}
                    onRemoveFromWorkgroupClicked={
                      handleOnRemoveFromWorkgroupClicked
                    }
                    onClicked={handleCardClick}
                    isBadgesEnabled={true}
                    ref={promptRefs.current.get(prompt.Id)}
                    aria-posinset={idx + 1}
                    aria-setsize={promptsToDisplay.length}
                    selectedGroupId={selectedPromptWorkgroup}
                    groups={groups}
                    isShowPromptAuthor={true}
                    handoffProduct={chooseBestProductForHandoff(prompt)}
                  />
                </Column>
              );
            })}
          </Row>
          <PromptDetailModal
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
            handleDialogClose={handleDialogClose}
            prompt={activePrompt as WebPrompt}
            morePrompts={morePrompts}
            onMoreLikeThisClicked={handleCardClick}
            onCopyClicked={onCopyPromptClicked}
            onTryInAppBadgeAction={onTryInAppBadgeAction}
            groups={groups}
            isShowPromptAuthor={true}
            isPromptCopyLinkEnabled={false}
            isPromptLikingEnabled={isFlightActive("isPromptLikingEnabled")}
            isBookmarkEnabled={isFlightActive("isPromptLikingEnabled")}
            onBookmarkToggled={onPromptBookmarkToggled}
            selectedGroupId={selectedDialogPromptWorkgroup}
            isPromptRemovingFromWorkgroupEnabled={true}
            onRemoveFromWorkgroupClicked={handleOnRemoveFromWorkgroupClicked}
          />
          <ShowMore
            isShowHasMore={isShowHasMore}
            hasMore={hasMore}
            isLoading={isLoading}
            handleCardFocus={handleCardFocus}
            onMoreClicked={onMoreClicked}
          />
        </>
      )}
    </>
  );
};
